import { useState } from 'react'
import { authFetch } from '../../lib/api'

interface SEOResult {
  score: number
  title: string | null
  description: string | null
  pageSize: number
  issues: Array<{ type: 'error' | 'warning' | 'info'; category: string; message: string }>
}

export function SEOPanel() {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<SEOResult | null>(null)

  const analyze = async () => {
    if (!url) return
    setLoading(true); setResult(null)
    try {
      const r = await authFetch('/api/seo/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      if (r.ok) setResult(await r.json())
    } catch {}
    setLoading(false)
  }

  const scoreColor = (s: number) => s >= 80 ? '#5fd4a4' : s >= 50 ? '#f5a157' : '#f07070'
  const typeIcon = (t: string) => t === 'error' ? '❌' : t === 'warning' ? '⚠️' : 'ℹ️'

  const s: React.CSSProperties = { padding: 20, height: '100%', overflow: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-1)' }
  const card: React.CSSProperties = { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }
  const input: React.CSSProperties = { flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', fontSize: 12, fontFamily: 'var(--font-mono)' }

  return (
    <div style={s}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--accent)' }}>📈 Growth / SEO</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 11 }}>Analyze your deployed app for SEO issues.</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input style={input} placeholder="https://your-app.pulse.dev" value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
        <button onClick={analyze} disabled={loading} style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
          {loading ? '⏳' : '🔍 Scan'}
        </button>
      </div>

      {result && (
        <>
          {/* Score */}
          <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', border: `3px solid ${scoreColor(result.score)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: scoreColor(result.score) }}>
              {result.score}
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>SEO Score</div>
              <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
                {result.title && <div>Title: {result.title.slice(0, 50)}</div>}
                <div>Page size: {result.pageSize}KB</div>
              </div>
            </div>
          </div>

          {/* Issues */}
          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Issues ({result.issues.length})
            </div>
            {result.issues.length === 0 ? (
              <div style={{ color: '#5fd4a4' }}>✅ No issues found!</div>
            ) : (
              result.issues.map((issue, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 12, flexShrink: 0 }}>{typeIcon(issue.type)}</span>
                  <div>
                    <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'var(--bg-3)', color: 'var(--text-2)', marginRight: 6 }}>{issue.category}</span>
                    <span style={{ fontSize: 11 }}>{issue.message}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}
